import { authSelectors, cloudSelectors } from '@readcloud/state';
import { Checkbox } from '@readcloud/ui/checkbox';
import { Input } from '@readcloud/ui';
import { Label } from '@readcloud/ui/label';
import * as React from 'react';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CloudsFieldData } from './schemas/formFields';
import { FuzzySearcher, useReadCloudAuth } from '@readcloud/common';
import { Cloud } from '@readcloud/data';
import { useDebounce } from 'use-debounce';

interface ISelectCloudsProps {}

const SelectClouds: React.FunctionComponent<ISelectCloudsProps> = ({}) => {
  const { user } = useReadCloudAuth();
  const userPermissions = useSelector(authSelectors.getUserPermissions);

  const clouds = useSelector(cloudSelectors.getClouds) || {};
  const allClouds = useSelector(cloudSelectors.getInstitutionClouds) || {};

  const availableClouds: Record<string, Cloud> = React.useMemo(() => {
    // Merge user's own clouds with institution clouds if they have permission
    if (userPermissions?.['institutionClouds']?.read) {
      return { ...clouds, ...allClouds };
    }
    return clouds;
  }, [clouds, allClouds, userPermissions]);
  const { control } = useFormContext<CloudsFieldData>();

  const sortedClouds = React.useMemo(
    () =>
      Object.values(availableClouds).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [availableClouds]
  );

  const [searchText, setSearchText] = React.useState('');
  const [debouncedSearchText] = useDebounce(searchText, 100);

  const cloudSearcher = React.useMemo(
    () =>
      new FuzzySearcher(sortedClouds, {
        keySelector: (cloud) => cloud.name,
      }),
    [sortedClouds]
  );

  const filteredClouds = React.useMemo(
    () =>
      debouncedSearchText
        ? cloudSearcher.search(debouncedSearchText)
        : sortedClouds,
    [debouncedSearchText, cloudSearcher, sortedClouds]
  );

  return (
    <div className="rc-flex rc-flex-col rc-gap-6">
      <Input
        name="cloud-search"
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
        placeholder="Search clouds"
      />
      <div className="rc-grid rc-grid-cols-2 rc-gap-y-1 rc-gap-x-6 rc-auto-rows-min">
        <Controller
          control={control}
          name="clouds"
          render={({ field: { onChange, value } }) => (
            <>
              {filteredClouds.map((cloud) => {
                return (
                  <div key={cloud.id} className="rc-flex rc-gap-2">
                    <Label className="rc-flex rc-gap-2">
                      <Checkbox
                        value={cloud.id}
                        checked={value?.includes(cloud.id)}
                        onCheckedChange={(checked) => {
                          const currentValue = value || [];
                          if (checked) {
                            onChange([...currentValue, cloud.id]);
                          } else {
                            onChange(
                              currentValue.filter((id) => id !== cloud.id)
                            );
                          }
                        }}
                      />
                      {cloud.name}
                    </Label>
                  </div>
                );
              })}
            </>
          )}
        />
      </div>
    </div>
  );
};

export default SelectClouds;
